import { useApi } from "src/hooks/api"
import { queryClient } from "./client"
import {
  getQueryKey,
  useDeleteQuery,
  useGetQuery,
  usePatchQuery,
  usePostQuery,
  useRouteParams
} from "./helpers"
import { UserParams } from "./types"

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

/** @returns /users/?page_size=100000 */
export const getUserListUrl = () => "/users/?page_size=100000"

/** @returns /users/${userUuid}/ */
export const getUserUrl = ({ userUuid }: Required<UserParams>) => `/users/${userUuid}/`

/** @returns "/manage/user/reset_password/" */
export const getResetUserPasswordUrl = () => "/manage/user/reset_password/"

/** @returns manage/user/${user_uuid}/organizations/ */
export const createUserOrganizationUrl = ({ userUuid }: Required<UserParams>) =>
  `/manage/user/${userUuid}/organizations/`

export const getEventsExportUrl = (
  startDate?: string /* ISO 8601 string */,
  endDate?: string /* ISO 8601 string */,
  orgUuid?: string,
  application?: string,
  poolUuid?: string,
  workspaceUuid?: string,
  resourceUuid?: string,
  messageContains?: string
) => {
  const url = `/manage/user/events/export/`
  const params = new URLSearchParams()
  if (startDate) params.append("start_date", startDate)
  if (endDate) params.append("end_date", endDate)
  if (orgUuid) params.append("org_uuid", orgUuid)
  if (application) params.append("application", application)
  if (poolUuid) params.append("pool_uuid", poolUuid)
  if (workspaceUuid) params.append("workspace_uuid", workspaceUuid)
  if (resourceUuid) params.append("resource_uuid", resourceUuid)
  if (messageContains) params.append("in_message", messageContains)

  return url + "?" + params.toString()
}

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const useUserList = () => {
  return useGetQuery<api.user.PaginatedUserList>("user", getUserListUrl())
}

export const useUser = (params?: UserParams) => {
  const mergedParams = useRouteParams(params)
  const url = getUserUrl(mergedParams)

  return useGetQuery<api.user.DetailedUser>("user", url)
}

export const useUpdateUser = (params?: UserParams) => {
  const mergedParams = useRouteParams(params)
  const url = getUserUrl(mergedParams)
  const listUrl = getUserListUrl()

  type TRequest = api.user.PatchedDetailedUserRequest
  type TResponse = api.user.DetailedUser

  return usePatchQuery<TRequest, TResponse>("user", url, [], {
    onSuccess: () =>
      // We remove users query instead of refetching it because the dataset in HUGE in production
      queryClient.removeQueries({ exact: true, queryKey: getQueryKey("user", listUrl) })
  })
}

export const useDeleteUser = (params?: UserParams) => {
  const mergedParams = useRouteParams(params)
  const url = getUserUrl(mergedParams)
  const listUrl = getUserListUrl()

  return useDeleteQuery("user", url, [listUrl])
}

export const useResetPassword = () => {
  const { jsonPost } = useApi("user")
  const url = getResetUserPasswordUrl()

  return {
    mutationFn: (data: api.user.ResetPassword) => jsonPost(url, data)
  }
}

export const useCreateUserOrganization = (params: Required<UserParams>) => {
  const url = createUserOrganizationUrl(params)
  const refetchUrls = [getUserUrl(params)]

  type TRequest = api.user.OrgPrivateCreationRequest
  type TResponse = api.user.OrganizationInfo

  return usePostQuery<TRequest, TResponse>("user", url, refetchUrls)
}
